import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { type IncludeKey, type Timeframe } from './useStudentGenerateMessageMutation';
import { timeframeToApiParams } from './studentDataParams';

const log = createLogger('useStudentDataLogQuery');

export const STUDENT_DATA_LOG_QUERY_KEY = 'studentDataLog';

export type StudentDataLogEvent = {
  type: string;
  context: string;
  timestamp: string;
  category?: string;
};

type StudentDataLogCounts = {
  warnings?: number;
  praises?: number;
  absences?: number;
};

type StudentDataLogResponse = {
  events: StudentDataLogEvent[];
  counts: StudentDataLogCounts;
};

export function useStudentDataLogQuery({
  studentId,
  periodId,
  periodCreatedAt,
  timeframe,
  include,
}: {
  studentId: string;
  periodId: string;
  periodCreatedAt: string;
  timeframe: Timeframe;
  include: IncludeKey[];
}) {
  const httpClient = useHttpClient();
  const locale = useLocale();

  const { data, error, status } = useQuery<StudentDataLogResponse | null>({
    queryKey: [STUDENT_DATA_LOG_QUERY_KEY, studentId, periodId, timeframe, periodCreatedAt, include],
    queryFn: async () => {
      log.debug('Fetching student data log', { studentId, periodId, timeframe });

      const tz = locale?.timeZone ?? '';
      const dateParams = timeframeToApiParams(timeframe, periodCreatedAt);

      const body: Record<string, unknown> = { studentId, periodId, tz, ...dateParams };
      body.include = include;

      const result = await httpClient.post<StudentDataLogResponse>('/report/getDataLog', body);
      log.info('Fetched student data log successfully', {
        studentId,
        eventCount: result.events.length,
      });
      return result;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch student data log', failureCount, error, log }),
    enabled: !!studentId && !!periodId,
  });

  return { data, error, status };
}
